import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Card } from 'react-bootstrap';
import { ReactComponent as EditIcon } from '../../../assets/images/Edit.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/images/Delete.svg';
import AddNewButton from '../../../Common/AddNewButton';
import Select from 'react-select';
import { addStanding, updateStanding, deleteStanding, getStandingById, Standing, StandingPayload } from '../../../api/tournament/standingsService';
import { listTeams } from '../../../api/tournament/teamService';
import { DeleteModal } from '../../../Common/DeleteModal';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import '../../../Common/ManageTournamentsFixturesTable.css';

interface StandingsTableProps {
  standings: Standing[];
  search: string;
  onSearch: (value: string) => void;
  onEdit: () => void;
  onDelete: () => void;
  onAdd: () => void;
  ageCategories: { value: string; label: string }[];
  tournamentId: string;
  standingsLoading: boolean;
  page: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  ageCategoryFilter?: any;
  onAgeCategoryFilterChange?: (filter: any) => void;
  groupFilter?: any;
  onGroupFilterChange?: (filter: any) => void;
  tournamentSports?: string; // Add sport prop for API calls
}

const PAGE_SIZE_OPTIONS = [10, 15, 20, 50];

const StandingsTable: React.FC<StandingsTableProps> = ({
  standings,
  search,
  onSearch,
  onEdit,
  onDelete,
  onAdd,
  ageCategories,
  tournamentId,
  standingsLoading,
  page,
  pageSize,
  totalPages,
  totalCount,
  onPageChange,
  onPageSizeChange,
  ageCategoryFilter,
  onAgeCategoryFilterChange,
  groupFilter,
  onGroupFilterChange,
  tournamentSports,
}) => {
  // Helper function to check if sport is Badminton or Table Tennis
  const isBadmintonOrTableTennis = () => {
    if (!tournamentSports) return false;
    const normalizedSport = tournamentSports.replace(/\s+/g, '').toLowerCase();
    return normalizedSport === 'badminton' || normalizedSport === 'tabletennis' || normalizedSport === 'tt';
  };
  
  const [showAddForm, setShowAddForm] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [filteredTeams, setFilteredTeams] = useState<{ value: string; label: string }[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [pendingEditData, setPendingEditData] = useState<any>(null);
  
  const [form, setForm] = useState<any>({
    team: null,
    ageCategory: null,
    group: '',
    matchesPlayed: 0,
    wins: 0,
    draws: 0,
    losses: 0,
    goalsFor: 0,
    goalsAgainst: 0,
    goalDifference: 0,
    points: 0,
    position: 1,
  });
  const [formTouched, setFormTouched] = useState<any>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [isFormModified, setIsFormModified] = useState(false);
  const [originalForm, setOriginalForm] = useState<any>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // Group options for filter
  const groupOptions = [
    { value: 'A', label: 'Group A' },
    { value: 'B', label: 'Group B' },
    { value: 'C', label: 'Group C' },
    { value: 'D', label: 'Group D' },
    { value: 'E', label: 'Group E' },
    { value: 'F', label: 'Group F' },
  ];

  // Debounced search
  const [inputValue, setInputValue] = useState(search);
  useEffect(() => {
    const handler = setTimeout(() => {
      onSearch(inputValue);
    }, 300);
    return () => clearTimeout(handler);
  }, [inputValue, onSearch]);

  useEffect(() => {
    if (!showAddForm) {
      setForm({
        team: null,
        ageCategory: null,
        group: '',
        matchesPlayed: 0,
        wins: 0,
        draws: 0,
        losses: 0,
        goalsFor: 0,
        goalsAgainst: 0,
        goalDifference: 0,
        points: 0,
        position: 1,
      });
      setFormTouched({});
      setFormError(null);
    }
  }, [showAddForm]);

  // Load teams based on selected age category
  const loadTeamsByAgeCategory = useCallback(async (ageCategory: string) => {
    if (!ageCategory || !tournamentId) {
      setFilteredTeams([]);
      return;
    }
    
    setTeamsLoading(true);
    try {
      const response = await listTeams({
        tournamentId: tournamentId,
        ageCategory,
        pageSize: 1000, // Get all teams for this age category
        sport: tournamentSports || undefined // Pass sport parameter
      });
      const teamsData = response.data?.paginatedResults || response.paginatedResults || [];
      const options = teamsData.map((team: any) => ({ 
        value: team._id, 
        label: team.name 
      }));
      setFilteredTeams(options);
    } catch (error) {
      console.error('Error loading teams by age category:', error);
      setFilteredTeams([]);
    } finally {
      setTeamsLoading(false);
    }
  }, [tournamentId]);

  // Load teams when age category changes
  useEffect(() => {
    if (form.ageCategory?.value) {
      loadTeamsByAgeCategory(form.ageCategory.value);
    } else {
      setFilteredTeams([]);
    }
  }, [form.ageCategory, tournamentId, loadTeamsByAgeCategory]);

  // Handle pending edit data when teams are loaded
  useEffect(() => {
    if (pendingEditData && filteredTeams.length > 0) {
      const formData = {
        team: filteredTeams.find(team => team.value === pendingEditData.team?._id) || null,
        ageCategory: ageCategories.find(opt => opt.value === pendingEditData.ageCategory?._id) || null,
        group: pendingEditData.group || '',
        matchesPlayed: pendingEditData.matchesPlayed || 0,
        wins: pendingEditData.wins || 0,
        draws: pendingEditData.draws || 0,
        losses: pendingEditData.losses || 0,
        goalsFor: pendingEditData.goalsFor || 0,
        goalsAgainst: pendingEditData.goalsAgainst || 0,
        goalDifference: pendingEditData.goalDifference || 0,
        points: pendingEditData.points || 0,
        position: pendingEditData.position || 1,
      };
      
      setForm(formData);
      setOriginalForm(JSON.parse(JSON.stringify(formData)));
      setIsFormModified(false);
      setFormTouched({});
      setFormError(null);
      setShowAddForm(true);
      setTimeout(scrollToCard, 100);
      setPendingEditData(null); // Clear pending data
    }
  }, [filteredTeams, pendingEditData, ageCategories]);

  // Calculate goal difference and points automatically
  useEffect(() => {
    const goalDifference = form.goalsFor - form.goalsAgainst;
    const points = (form.wins * 3) + (form.draws * 1);
    
    setForm((prev: any) => ({
      ...prev,
      goalDifference,
      points
    }));
  }, [form.goalsFor, form.goalsAgainst, form.wins, form.draws]);

  const validateForm = () => {
    return {
      team: !form.team ? 'Team is required.' : '',
      ageCategory: !form.ageCategory ? 'Age Category is required.' : '',
      group: !form.group ? 'Group is required.' : '',
      matchesPlayed: form.matchesPlayed < 0 ? 'Matches played cannot be negative.' : '',
      wins: form.wins < 0 ? 'Wins cannot be negative.' : '',
      draws: form.draws < 0 ? 'Draws cannot be negative.' : '',
      losses: form.losses < 0 ? 'Losses cannot be negative.' : '',
      goalsFor: form.goalsFor < 0 ? 'Goals for cannot be negative.' : '',
      goalsAgainst: form.goalsAgainst < 0 ? 'Goals against cannot be negative.' : '',
      position: form.position < 1 ? 'Position must be at least 1.' : '',
    };
  };
  const errors = validateForm();
  const isFormValid = Object.values(errors).every(x => !x);

  // Check if form has been modified
  const checkFormModified = () => {
    if (!originalForm) return false;
    return (
      form.team !== originalForm.team ||
      form.ageCategory !== originalForm.ageCategory ||
      form.group !== originalForm.group ||
      form.matchesPlayed !== originalForm.matchesPlayed ||
      form.wins !== originalForm.wins ||
      form.draws !== originalForm.draws ||
      form.losses !== originalForm.losses ||
      form.goalsFor !== originalForm.goalsFor ||
      form.goalsAgainst !== originalForm.goalsAgainst ||
      form.position !== originalForm.position
    );
  };

  React.useEffect(() => {
    setIsFormModified(checkFormModified());
  }, [form, originalForm]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numValue = ['matchesPlayed', 'wins', 'draws', 'losses', 'goalsFor', 'goalsAgainst', 'position'].includes(name) 
      ? parseInt(value) || 0 
      : value;
    setForm({ ...form, [name]: numValue });
  };

  const handleSelectChange = (name: string, option: any) => {
    if (name === 'ageCategory') {
      // Clear team selection when age category changes
      setForm({ 
        ...form, 
        [name]: option, 
        team: null
      });
    } else {
      setForm({ ...form, [name]: option });
    }
    setFormTouched({ ...formTouched, [name]: true });
  };

  const handleGroupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 1);
    setForm({ ...form, group: value });
    setFormTouched({ ...formTouched, group: true });
  };

  const handleAdd = () => {
    setEditIndex(null);
    const initialForm = {
      team: null,
      ageCategory: null,
      group: '',
      matchesPlayed: 0,
      wins: 0,
      draws: 0,
      losses: 0,
      goalsFor: 0,
      goalsAgainst: 0,
      goalDifference: 0,
      points: 0,
      position: 1,
    };
    setForm(initialForm);
    setOriginalForm(initialForm);
    setIsFormModified(false);
    setFormTouched({});
    setFormError(null);
    setShowAddForm(true);
    setTimeout(scrollToCard, 100);
  };

  const handleEdit = async (idx: number) => {
    setEditIndex(idx);
    const s = standings[idx];
    
    try {
      // Fetch detailed standing data
      const detailedStanding = await getStandingById(s._id);
      console.log('Full API response:', detailedStanding);
      
      // Handle different response structures
      const standingData = detailedStanding.data || detailedStanding;
      console.log('Extracted standing data:', standingData);
      
      if (!standingData) {
        throw new Error('No standing data received from API');
      }
      
      // Store the standing data to be processed when teams are loaded
      setPendingEditData(standingData);
      
      // Load teams for the age category
      const ageCategoryValue = standingData.ageCategory?.ageCategory;
      if (ageCategoryValue) {
        await loadTeamsByAgeCategory(ageCategoryValue);
      }
    } catch (error) {
      console.error('Error fetching standing details:', error);
      console.log('Using fallback data from list item:', s);
      
      // Even if API fails, open form with basic data from the list
      const formData = {
        team: filteredTeams.find(team => team.value === s.team?._id) || null,
        ageCategory: ageCategories.find(opt => opt.value === s.ageCategory?._id) || null,
        group: s.group || '',
        matchesPlayed: s.matchesPlayed || 0,
        wins: s.wins || 0,
        draws: s.draws || 0,
        losses: s.losses || 0,
        goalsFor: s.goalsFor || 0,
        goalsAgainst: s.goalsAgainst || 0,
        goalDifference: s.goalDifference || 0,
        points: s.points || 0,
        position: s.position || 1,
      };
      
      setForm(formData);
      setOriginalForm(JSON.parse(JSON.stringify(formData)));
      setIsFormModified(false);
      setFormTouched({});
      setFormError(null);
      setShowAddForm(true);
      setTimeout(scrollToCard, 100);
    }
  };

  const handleDelete = (idx: number) => {
    setDeleteIndex(idx);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (deleteIndex !== null) {
      const standing = standings[deleteIndex];
      try {
        if (!tournamentSports) {
          throw new Error('Tournament sport is required to modify standings.');
        }
        await deleteStanding(standing._id, tournamentSports);
        onDelete();
        setShowDeleteModal(false);
        setDeleteIndex(null);
      } catch (error) {
        console.error('Error deleting standing:', error);
        setFormError('Failed to delete standing');
      }
    }
  };

  const scrollToCard = () => {
    if (cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleClose = () => setShowAddForm(false);

  const handleSave = async () => {
    setFormTouched({
      team: true,
      ageCategory: true,
      group: true,
      matchesPlayed: true,
      wins: true,
      draws: true,
      losses: true,
      goalsFor: true,
      goalsAgainst: true,
      position: true,
    });
    if (!isFormValid) return;
    if (!tournamentSports) {
      setFormError('Tournament sport is required to modify standings.');
      return;
    }
    setFormError(null);
    setSaving(true);
    try {
      const payload: StandingPayload = {
        tournament: tournamentId,
        team: form.team.value,
        ageCategory: form.ageCategory.value,
        group: form.group,
        matchesPlayed: form.matchesPlayed,
        wins: form.wins,
        draws: form.draws,
        losses: form.losses,
        goalsFor: form.goalsFor,
        goalsAgainst: form.goalsAgainst,
        goalDifference: form.goalDifference,
        points: form.points,
        position: form.position,
      };
      
      if (editIndex !== null) {
        await updateStanding(standings[editIndex]._id, payload, tournamentSports);
      } else {
        await addStanding(payload, tournamentSports);
      }
      
      setShowAddForm(false);
      setEditIndex(null);
      setFormError(null);
      onAdd();
    } catch (error) {
      setFormError('Failed to save standing. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (standingsLoading) {
    return <ShimmerLoader type="table" />;
  }

  return (
    <div>
      {/* Search and Filter Bar */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-3">
          <h4 className="mb-0">Standings</h4>
          <div className="search-box">
            <input
              type="text"
              className="form-control"
              placeholder="Search standings..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              style={{ minWidth: '250px', paddingLeft: '12px' }}
            />
          </div>
          <div className="filter-box">
            <Select
              classNamePrefix="react-select"
              value={ageCategoryFilter}
              onChange={onAgeCategoryFilterChange}
              options={ageCategories}
              placeholder="Age Category"
              isClearable
              styles={{ control: base => ({ ...base, minWidth: '150px' }) }}
            />
          </div>
          <div className="filter-box">
            <Select
              classNamePrefix="react-select"
              value={groupFilter}
              onChange={onGroupFilterChange}
              options={groupOptions}
              placeholder="Group"
              isClearable
              styles={{ control: base => ({ ...base, minWidth: '120px' }) }}
            />
          </div>
        </div>
        <AddNewButton onClick={handleAdd} />
      </div>

      {/* Add/Edit Form Card */}
      {showAddForm && (
        <Card ref={cardRef} className="mb-4" style={{ border: '1px solid #e3e6f0' }}>
          <Card.Header className="d-flex justify-content-between align-items-center" style={{ backgroundColor: '#f8f9fc' }}>
            <h6 className="mb-0">{editIndex !== null ? 'Edit Standing' : 'Add New Standing'}</h6>
            <Button variant="link" onClick={handleClose} className="p-0">
              <i className="ri-close-line" style={{ fontSize: '1.5rem' }}></i>
            </Button>
          </Card.Header>
          <Card.Body>
            {formError && <div className="alert alert-danger">{formError}</div>}
            <Form>
              <div className="row">
                <div className="col-md-4">
                  <Form.Group className="mb-3">
                    <Form.Label>Age Category<span style={{ color: 'red' }}>*</span></Form.Label>
                    <Select
                      classNamePrefix="react-select"
                      name="ageCategory"
                      value={form.ageCategory}
                      onChange={option => handleSelectChange('ageCategory', option)}
                      options={ageCategories}
                      placeholder="Select Age Category"
                      isClearable={false}
                      styles={{ control: base => ({ ...base, minHeight: '38px' }) }}
                    />
                    {(errors.ageCategory && formTouched.ageCategory) && <div className="text-danger">{errors.ageCategory}</div>}
                  </Form.Group>
                </div>
                <div className="col-md-4">
                  <Form.Group className="mb-3">
                    <Form.Label>Team<span style={{ color: 'red' }}>*</span></Form.Label>
                    <Select
                      classNamePrefix="react-select"
                      name="team"
                      value={form.team}
                      onChange={option => handleSelectChange('team', option)}
                      options={filteredTeams}
                      placeholder={form.ageCategory ? "Select Team" : "Select Age Category first"}
                      isClearable={false}
                      isLoading={teamsLoading}
                      isDisabled={!form.ageCategory}
                      styles={{ control: base => ({ ...base, minHeight: '38px' }) }}
                    />
                    {(errors.team && formTouched.team) && <div className="text-danger">{errors.team}</div>}
                  </Form.Group>
                </div>
                <div className="col-md-4">
                  <Form.Group className="mb-3">
                    <Form.Label>Group<span style={{ color: 'red' }}>*</span></Form.Label>
                    <Form.Control 
                      name="group" 
                      value={form.group} 
                      onChange={handleGroupChange} 
                      placeholder="Enter Group (A-Z)"
                      maxLength={1}
                      isInvalid={!!errors.group && formTouched.group} 
                      onBlur={() => setFormTouched({ ...formTouched, group: true })} 
                    />
                    {(errors.group && formTouched.group) && <div className="text-danger">{errors.group}</div>}
                  </Form.Group>
                </div>
              </div>
              <div className="row">
                <div className="col-md-3">
                  <Form.Group className="mb-3">
                    <Form.Label>Position<span style={{ color: 'red' }}>*</span></Form.Label>
                    <Form.Control 
                      name="position" 
                      type="number"
                      min="1"
                      value={form.position} 
                      onChange={handleInputChange} 
                      isInvalid={!!errors.position && formTouched.position} 
                      onBlur={() => setFormTouched({ ...formTouched, position: true })} 
                    />
                    {(errors.position && formTouched.position) && <div className="text-danger">{errors.position}</div>}
                  </Form.Group>
                </div>
                <div className="col-md-3">
                  <Form.Group className="mb-3">
                    <Form.Label>Matches Played</Form.Label>
                    <Form.Control 
                      name="matchesPlayed" 
                      type="number"
                      min="0"
                      value={form.matchesPlayed} 
                      onChange={handleInputChange} 
                      isInvalid={!!errors.matchesPlayed && formTouched.matchesPlayed} 
                      onBlur={() => setFormTouched({ ...formTouched, matchesPlayed: true })} 
                    />
                    {(errors.matchesPlayed && formTouched.matchesPlayed) && <div className="text-danger">{errors.matchesPlayed}</div>}
                  </Form.Group>
                </div>
                <div className="col-md-3">
                  <Form.Group className="mb-3">
                    <Form.Label>Wins</Form.Label>
                    <Form.Control 
                      name="wins" 
                      type="number"
                      min="0"
                      value={form.wins} 
                      onChange={handleInputChange} 
                      isInvalid={!!errors.wins && formTouched.wins} 
                      onBlur={() => setFormTouched({ ...formTouched, wins: true })} 
                    />
                    {(errors.wins && formTouched.wins) && <div className="text-danger">{errors.wins}</div>}
                  </Form.Group>
                </div>
                <div className="col-md-3">
                  <Form.Group className="mb-3">
                    <Form.Label>Draws</Form.Label>
                    <Form.Control 
                      name="draws" 
                      type="number"
                      min="0"
                      value={form.draws} 
                      onChange={handleInputChange} 
                      isInvalid={!!errors.draws && formTouched.draws} 
                      onBlur={() => setFormTouched({ ...formTouched, draws: true })} 
                    />
                    {(errors.draws && formTouched.draws) && <div className="text-danger">{errors.draws}</div>}
                  </Form.Group>
                </div>
              </div>
              <div className="row">
                <div className="col-md-3">
                  <Form.Group className="mb-3">
                    <Form.Label>Losses</Form.Label>
                    <Form.Control 
                      name="losses" 
                      type="number"
                      min="0"
                      value={form.losses} 
                      onChange={handleInputChange} 
                      isInvalid={!!errors.losses && formTouched.losses} 
                      onBlur={() => setFormTouched({ ...formTouched, losses: true })} 
                    />
                    {(errors.losses && formTouched.losses) && <div className="text-danger">{errors.losses}</div>}
                  </Form.Group>
                </div>
                <div className="col-md-3">
                  <Form.Group className="mb-3">
                    <Form.Label>Goals For</Form.Label>
                    <Form.Control 
                      name="goalsFor" 
                      type="number"
                      min="0"
                      value={form.goalsFor} 
                      onChange={handleInputChange} 
                      isInvalid={!!errors.goalsFor && formTouched.goalsFor} 
                      onBlur={() => setFormTouched({ ...formTouched, goalsFor: true })} 
                    />
                    {(errors.goalsFor && formTouched.goalsFor) && <div className="text-danger">{errors.goalsFor}</div>}
                  </Form.Group>
                </div>
                <div className="col-md-3">
                  <Form.Group className="mb-3">
                    <Form.Label>Goals Against</Form.Label>
                    <Form.Control 
                      name="goalsAgainst" 
                      type="number"
                      min="0"
                      value={form.goalsAgainst} 
                      onChange={handleInputChange} 
                      isInvalid={!!errors.goalsAgainst && formTouched.goalsAgainst} 
                      onBlur={() => setFormTouched({ ...formTouched, goalsAgainst: true })} 
                    />
                    {(errors.goalsAgainst && formTouched.goalsAgainst) && <div className="text-danger">{errors.goalsAgainst}</div>}
                  </Form.Group>
                </div>
                <div className="col-md-3">
                  <Form.Group className="mb-3">
                    <Form.Label>Goal Difference</Form.Label>
                    <Form.Control 
                      name="goalDifference" 
                      type="number"
                      value={form.goalDifference} 
                      readOnly
                      style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                    />
                  </Form.Group>
                </div>
              </div>
              <div className="row">
                <div className="col-md-3">
                  <Form.Group className="mb-3">
                    <Form.Label>Points</Form.Label>
                    <Form.Control 
                      name="points" 
                      type="number"
                      value={form.points} 
                      readOnly
                      style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                    />
                  </Form.Group>
                </div>
              </div>
              <div className="d-flex justify-content-end gap-2">
                <Button variant="outline-secondary" onClick={handleClose}>
                  Cancel
                </Button>
                <Button 
                  variant="primary" 
                  onClick={handleSave} 
                  disabled={!isFormValid || saving || !isFormModified}
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      )}

      {/* Standings Table */}
      <div className="manage-tournaments-table-container">
        <Table className="mb-0 manage-tournaments-table standings-table">
          <thead>
            <tr>
              <th>Position</th>
              <th>Team</th>
              <th>Age Category</th>
              <th>Group</th>
              <th>MP</th>
              <th>W</th>
              <th>D</th>
              <th>L</th>
              <th>GF</th>
              <th>GA</th>
              <th>GD</th>
              <th>Points</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {standingsLoading ? (
              <tr>
                <td colSpan={13}><ShimmerLoader type="table" rows={5} /></td>
              </tr>
            ) : !standings || standings.length === 0 ? (
              <tr><td colSpan={13} className="text-center py-4">No standings found.</td></tr>
            ) : (
              standings.map((standing, index) => (
              <tr key={standing._id}>
                <td>
                  <span className="badge bg-primary">{standing.position}</span>
                </td>
                <td>{standing.team?.name || '-'}</td>
                <td>{standing.ageCategory?.ageCategory || '-'}</td>
                <td>
                  <span className="badge bg-secondary">{standing.group || '-'}</span>
                </td>
                <td>{standing.matchesPlayed}</td>
                <td>{standing.wins}</td>
                <td>{standing.draws}</td>
                <td>{standing.losses}</td>
                <td>{standing.goalsFor}</td>
                <td>{standing.goalsAgainst}</td>
                <td>
                  <span className={`badge ${standing.goalDifference >= 0 ? 'bg-success' : 'bg-danger'}`}>
                    {standing.goalDifference >= 0 ? '+' : ''}{standing.goalDifference}
                  </span>
                </td>
                <td>
                  <span className="badge bg-warning text-dark">{standing.points}</span>
                </td>
                <td>
                  <div className="tournament-actions">
                    <button
                      className="action-btn btn-view"
                      title="View"
                      type="button"
                    >
                      <i className="ri-eye-line"></i>
                    </button>
                    {/* Hide edit and delete buttons for Badminton and Table Tennis */}
                    {!isBadmintonOrTableTennis() && (
                      <>
                        <button
                          className="action-btn btn-edit"
                          onClick={() => handleEdit(index)}
                          title="Edit"
                          type="button"
                        >
                          <i className="ri-pencil-line"></i>
                        </button>
                        <button
                          className="action-btn btn-delete"
                          onClick={() => handleDelete(index)}
                          title="Delete"
                          type="button"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-section mt-4" style={{ 
          padding: '16px 0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div className="page-info mb-3">
            <span className="text-muted" style={{ 
              fontSize: '14px', 
              fontWeight: '500',
              color: '#6c757d'
            }}>
              Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalCount)} of {totalCount} standings
            </span>
          </div>
          <div className="pagination-controls d-flex align-items-center">
            <div className="page-numbers d-flex align-items-center">
              <Button 
                variant="outline-secondary" 
                size="sm" 
                onClick={() => onPageChange(page - 1)} 
                disabled={page === 1}
                style={{ marginRight: '8px' }}
              >
                Previous
              </Button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else {
                  const startPage = Math.max(1, Math.min(page - 2, totalPages - 4));
                  pageNum = startPage + i;
                }
                return (
                  <Button
                    key={pageNum}
                    variant={pageNum === page ? "primary" : "outline-secondary"}
                    size="sm"
                    onClick={() => onPageChange(pageNum)}
                    style={{ marginRight: '4px', minWidth: '32px' }}
                  >
                    {pageNum}
                  </Button>
                );
              })}
              <Button 
                variant="outline-secondary" 
                size="sm" 
                onClick={() => onPageChange(page + 1)} 
                disabled={page === totalPages}
                style={{ marginLeft: '8px' }}
              >
                Next
              </Button>
            </div>
            <div className="page-size-selector ms-4">
              <Form.Select 
                size="sm" 
                value={pageSize} 
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                style={{ minWidth: '80px' }}
              >
                {PAGE_SIZE_OPTIONS.map(size => (
                  <option key={size} value={size}>{size} per page</option>
                ))}
              </Form.Select>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        deleteModalFunction={confirmDelete}
        title="Delete Standing"
        message="Are you sure you want to delete this standing? This action cannot be undone."
      />
    </div>
  );
};

export default StandingsTable;
